import styled from 'styled-components';
import {
  cssColorValue,
  cssSpacing,
  TableCell,
  TableRow,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { DeliveryError } from '@hooks/useAlbumInfo/types';
import { Table } from '@components/shared/Table';

const Container = styled.div`
  margin-top: ${cssSpacing('base', '24px')};
`;

const StyledTableCell = styled(TableCell)`
  background-color: ${cssColorValue('backgroundHighlight')};
  vertical-align: initial;
  padding-top: ${cssSpacing('tighter-3', '12px')};
  padding-bottom: ${cssSpacing('tighter-3', '12px')};
`;

interface DeliveryErrorsProps {
  deliveryErrors: DeliveryError[];
}

export const DeliveryErrors = ({ deliveryErrors }: DeliveryErrorsProps) => {
  const t = useT();

  const formatErrorType = (errorType: string) => {
    switch (errorType) {
      case 'XML':
        return t(
          'atlas.delivery.error_types.xml',
          'XML error',
          `A type of delivery error related to XML. The word 'XML' can be kept in English for every translation`,
        );
      case 'AUDIO':
        return t(
          'atlas.delivery.error_types.audio',
          'Audio error',
          'A type of delivery error related to Audio',
        );
      case 'IMAGE':
        return t(
          'atlas.delivery.error_types.image',
          'Image error',
          'A type of delivery error related to Images',
        );
      case 'BATCH':
        return t(
          'atlas.delivery.error_types.batch',
          'Batch error',
          'A type of delivery error related to the batch',
        );
      default:
        return t(
          'atlas.delivery.error_types.unknown',
          'Unknown error',
          'A type of delivery error that is Unknown',
        );
    }
  };

  return (
    <Container>
      <Type
        as="p"
        variant="cello"
        semanticColor="textBase"
        paddingBottom={cssSpacing('base', '24px')}
      >
        {t(
          'atlas.delivery_errors',
          'Delivery errors',
          'Title for Delivery errors section',
        )}
      </Type>
      <Table
        widths={['20%', '80%']}
        headers={[
          t(
            'atlas.error_type',
            'Error type',
            `Text indicating the type of error. I.e 'Audio error', 'Image error', etc.`,
          ),
          t(
            'atlas.error_details',
            'Error details',
            'Text indicating error details',
          ),
        ]}
      >
        {deliveryErrors.map((deliveryError, index) => (
          <TableRow key={`deliveryError-${index}`}>
            <StyledTableCell>
              <Type
                dangerouslySetInnerHTML={{
                  __html: formatErrorType(deliveryError.type),
                }}
              />
            </StyledTableCell>
            <StyledTableCell>
              <Type
                dangerouslySetInnerHTML={{
                  __html: deliveryError.message,
                }}
              />
            </StyledTableCell>
          </TableRow>
        ))}
      </Table>
    </Container>
  );
};
